import apiClient from '../client';
import type { AgentSchema } from '@appTypes';

// SAV Agents API
export type { AgentSchema } from '@appTypes';

// ============== VALIDATED AGENTS ==============

/**
 * Fetch paginated validated agents
 */
export async function fetchPaginatedValidatedAgents(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: AgentSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/sav/agents/validated', {
    params: { lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

// ============== PENDING AGENTS ==============

/**
 * Fetch paginated pending agents
 */
export async function fetchPaginatedPendingAgents(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: AgentSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/sav/agents/pending', {
    params: { lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

// ============== ON-HOLD AGENTS ==============

/**
 * Fetch paginated on-hold agents
 */
export async function fetchPaginatedOnHoldAgents(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: AgentSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/sav/agents/on-hold', {
    params: { lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

// ============== REJECTED AGENTS ==============

/**
 * Fetch paginated rejected agents
 */
export async function fetchPaginatedRejectedAgents(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: AgentSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/sav/agents/rejected', {
    params: { lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

// ============== ARCHIVED AGENTS ==============

/**
 * Fetch paginated archived and validated agents
 */
export async function fetchPaginatedValidatedAndArchivedAgents(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: AgentSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/sav/agents/archived', {
    params: { lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

// ============== AGENT ACTIONS ==============

/**
 * Validate an agent's identity
 */
export async function validateAgentIdentity(params: { agent: AgentSchema }): Promise<void> {
  await apiClient.post(`/sav/agents/${params.agent.userId}/validate`, {
    agent: params.agent,
  });
}

/**
 * Reject an agent's identity
 */
export async function rejectAgentIdentity(params: { agent: AgentSchema }): Promise<void> {
  await apiClient.post(`/sav/agents/${params.agent.userId}/reject`, {
    agent: params.agent,
  });
}

/**
 * Put an agent's identity on hold
 */
export async function putAgentIdentityOnHold(params: { agent: AgentSchema }): Promise<void> {
  await apiClient.post(`/sav/agents/${params.agent.userId}/on-hold`, {
    agent: params.agent,
  });
}

/**
 * Archive a validated agent
 */
export async function archiveValidatedAgent(params: { agent: AgentSchema }): Promise<void> {
  await apiClient.post(`/sav/agents/${params.agent.userId}/archive`, {
    agent: params.agent,
  });
}

/**
 * Unarchive a validated agent
 */
export async function unArchiveValidatedAgent(params: { agent: AgentSchema }): Promise<void> {
  await apiClient.post(`/sav/agents/${params.agent.userId}/unarchive`, {
    agent: params.agent,
  });
}

/**
 * Delete a user (hard delete for rejected agents)
 */
export async function deleteUser(params: { agent: AgentSchema }): Promise<void> {
  await apiClient.delete(`/sav/agents/${params.agent.userId}`, {
    data: { agent: params.agent },
  });
}

/**
 * Export agents as Excel (.xlsx).
 * identityStatus defaults to 'Validated'.
 */
export async function exportAgents(params: { identityStatus?: string } = {}): Promise<void> {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';
  const token = localStorage.getItem('authToken');

  const qs = params.identityStatus ? `?identityStatus=${params.identityStatus}` : '';
  const response = await fetch(`${apiBaseUrl}/sav/agents/export${qs}`, {
    method: 'GET',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to export agents');
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  let fileName = `agents_${new Date().toISOString().slice(0, 10)}.xlsx`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match?.[1]) fileName = decodeURIComponent(match[1].replace(/['"]/g, ''));
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
